import React, { useEffect } from "react";
import { graphql, Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import Page from "~/models/page";
import News from "~/models/news";
import Project, { ProjectYamlType } from "~/models/project";
import Frame from "~/views/layout/frame";
import SEO from "~/views/components/generic/seo";
import structuredData from "~/helpers/structured-data";
import Navigation from "~/views/compositions/navigation";
import ProjectCard from "~/views/components/project/card";
import ArrowCircleLeft from "~/views/identity/arrow-circle-left";
import ArrowCircleRight from "~/views/identity/arrow-circle-right";
import ListItem from "~/views/components/news/list-item";

type Props = {
  data: {
    page: any;
    news: {
      nodes: any[];
    };
    projects: {
      nodes: ProjectYamlType[];
    };
  };
};

const HomePage = ({ data }: Props) => {
  // GraphQLデータからProjectインスタンスを作成
  const projects = (data.projects?.nodes || []).map(
    (projectData: ProjectYamlType) => new Project(projectData)
  );
  const news = (data.news?.nodes || []).map((n: any) => new News(n as any));

  useEffect(() => {
    // カルーセルの初期位置を2番目のアイテムに設定
    const carousel = document.getElementById("projects-carousel");
    if (carousel) {
      // 画面サイズに応じて2番目のアイテムの位置を計算
      const getSecondItemPosition = () => {
        if (window.innerWidth < 640) {
          // モバイル: 320px (カード幅) + 16px (gap) = 336px
          return 336;
        } else if (window.innerWidth < 1024) {
          // タブレット: 380px (カード幅) + 24px (gap) = 404px
          return 404;
        } else {
          // デスクトップ: 464px (カード幅) + 32px (gap) = 496px
          return 496;
        }
      };

      const setInitialPosition = () => {
        carousel.scrollLeft = getSecondItemPosition();
      };

      // 初期位置を設定
      setInitialPosition();

      // リサイズ時に位置を再計算
      const handleResize = () => {
        setInitialPosition();
      };

      window.addEventListener("resize", handleResize);

      // クリーンアップ
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  return (
    <Frame>
      <Navigation />

      {/* Hero Section */}
      <section className="flex flex-col lg:flex-row gap-8 lg:gap-0 items-start justify-center px-container py-6 sm:py-8 lg:py-10 w-full mx-auto max-w-7xl">
        {/* Who We Are Section */}
        <div className="flex flex-col gap-4 sm:gap-5 w-full lg:max-w-[640px] px-0 w1140:px-8">
          <div className="flex flex-col gap-4 sm:gap-5">
            <div className="flex flex-col gap-4 sm:gap-5">
              <h2 className="text-lg sm:text-xl leading-[32px] sm:leading-[44px] font-[800]">
                Who We Are
              </h2>
              <h1 className="font-[800] text-[24px] sm:text-[32px] lg:text-[40px] leading-[42px] sm:leading-[48px] lg:leading-[74px] palt">
                ビジネスの成長を加速させる
                <br />
                デジタル領域での
                <br />
                ソリューション開発を専門とした
                <br />
                プロダクトデザインスタジオです。
              </h1>
            </div>
            <p className="font-[400] text-sm sm:text-lg leading-[2em] sm:leading-[2] palt tracking-[0.03em]">
              私たちはプロダクト戦略とソフトウェアデザインを専門とする、デジタルプロダクトデザインの会社です。動画配信サービスやZ世代向けエンタメ、ファッションECなど、国内外の幅広い分野でのソリューション開発を行なっています。
            </p>
          </div>

          {/* About Button */}
          <a
            href="/about-us/"
            className="inline-flex items-center gap-2.5 px-4 sm:px-5 py-2.5 border border-black rounded-[30px] bg-white hover:opacity-70 transition-opacity w-fit"
          >
            <span className="font-medium text-base sm:text-lg">
              Artefactについて
            </span>
            <svg
              width="20"
              height="20"
              className="sm:w-6 sm:h-6"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 12H19"
                stroke="#121212"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 5L19 12L12 19"
                stroke="#121212"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>

        {/* Latest News Section */}
        <div className="flex flex-col gap-0 w-full lg:max-w-[456px]">
          <div className="flex items-center justify-between px-0 lg:px-8 mb-3">
            <h2 className="text-lg sm:text-xl leading-[32px] sm:leading-[44px] font-[800]">
              Latest News
            </h2>
            <Link to="/news/" className="hover:opacity-70 transition-opacity">
              <ArrowCircleRight />
            </Link>
          </div>

          <Link
            to="/news/digital-product-design-textbook/"
            className="group flex flex-col gap-6 sm:gap-8 px-0 lg:px-8 py-4 bg-white sm:hover:bg-neutral-99 rounded-[24px] sm:rounded-[40px] transition-colors"
          >
            {/* News Image */}
            <div className="relative">
              <div className="bg-neutral-99 rounded-[24px] sm:rounded-[40px] w-full h-[280px] sm:h-[320px] lg:h-[392px] flex items-center justify-center">
                <div className="bg-white shadow-[0px_4px_20px_0px_rgba(0,0,0,0.04)] group-hover:shadow-[0px_4px_20px_0px_rgba(0,0,0,0.12)] w-[180px] sm:w-[200px] lg:w-[231px] h-[240px] sm:h-[280px] lg:h-[328px] rounded-sm overflow-hidden">
                  <StaticImage
                    src="../../../assets/images/digital-product-design-textbook.png"
                    alt="デジタルプロダクトデザインの教科書"
                    placeholder="blurred"
                    layout="constrained"
                    width={231}
                    height={328}
                    className="w-full h-full object-cover border border-neutral-99"
                  />
                </div>
              </div>
            </div>

            {/* News Content */}
            <div className="flex flex-col gap-2 sm:gap-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                <h3 className="font-[500] text-[16px] sm:text-[18px] lg:text-[20px]">
                  書籍出版のお知らせ
                </h3>
                <time className="font-[300] text-[12px] sm:text-[14px]">
                  2025年11月12日
                </time>
              </div>
              <p className="font-[350] text-sm sm:text-base leading-6 sm:leading-8 palt tracking-[0.02em]">
                翔泳社の新レーベルUODBから、弊社代表取締役の菅沼が執筆した『デジタルプロダクトデザインの教科書』が刊行されました。
              </p>
            </div>
          </Link>
        </div>
      </section>

      {/* Projects Section */}
      <section className="flex flex-col gap-4 sm:gap-5 items-center py-6 sm:py-8 lg:py-10 w-full">
        {/* Section Header */}
        <div className="flex items-center justify-between w-full px-container">
          <h2 className="text-lg sm:text-xl leading-[32px] sm:leading-[44px] font-[800]">
            Projects & Works
          </h2>

          <div className="flex items-center gap-8 sm:gap-12 lg:gap-16">
            <button
              className="w-8 h-8 sm:w-10 sm:h-10 lg:w-11 lg:h-11 flex items-center justify-center hover:opacity-70 transition-opacity"
              onClick={() => {
                const carousel = document.getElementById("projects-carousel");
                if (carousel) {
                  const scrollAmount =
                    window.innerWidth < 640
                      ? -320
                      : window.innerWidth < 1024
                      ? -400
                      : -464;
                  carousel.scrollBy({ left: scrollAmount, behavior: "smooth" });
                }
              }}
              aria-label="Previous projects"
            >
              <ArrowCircleLeft />
            </button>
            <button
              className="w-8 h-8 sm:w-10 sm:h-10 lg:w-11 lg:h-11 flex items-center justify-center hover:opacity-70 transition-opacity"
              onClick={() => {
                const carousel = document.getElementById("projects-carousel");
                if (carousel) {
                  const scrollAmount =
                    window.innerWidth < 640
                      ? 320
                      : window.innerWidth < 1024
                      ? 400
                      : 464;
                  carousel.scrollBy({ left: scrollAmount, behavior: "smooth" });
                }
              }}
              aria-label="Next projects"
            >
              <ArrowCircleRight />
            </button>
          </div>
        </div>

        {/* Carousel Container */}
        <div className="w-full">
          <div
            id="projects-carousel"
            className="flex overflow-x-auto scroll-smooth snap-x snap-proximity scrollbar-hide px-0 lg:px-[calc((100vw-1096px)/2)] scroll-px-0 lg:scroll-px-[calc((100vw-1096px)/2)]"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {projects.map((project) => (
              <div key={project.id} className="snap-start flex-shrink-0">
                <ProjectCard
                  project={project}
                  className="max-w-[320px] sm:max-w-[380px] lg:max-w-[464px]"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="flex flex-col gap-[10px] items-center px-0 py-6 sm:py-8 lg:py-10 w-full">
        {/* Section Header */}
        <div className="flex items-center justify-between w-full px-container">
          <h2 className="text-lg sm:text-xl leading-[32px] sm:leading-[44px] font-[800]">
            News
          </h2>
          <div className="hover:opacity-70 transition-opacity">
            <Link to="/news/">
              <ArrowCircleRight />
            </Link>
          </div>
        </div>

        {/* News List */}
        <div className="flex flex-col items-start w-full palt tracking-[0.02em] px-0 max-w-[1096px]">
          {news.map((n: any) => (
            <ListItem
              key={n.id}
              url={n.url}
              createdAt={n.createdAt}
              title={n.title}
              description={n.description}
            />
          ))}
        </div>
      </section>

      {/* Get in Touch Section */}
      <section className="flex flex-col gap-4 items-center py-6 sm:py-8 lg:py-10 w-full bg-neutral-99">
        {/* Section Header */}
        <div className="flex items-center justify-between w-full px-container">
          <h2 className="text-lg sm:text-xl leading-[32px] sm:leading-[44px] font-[800]">
            Get in Touch
          </h2>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-8 sm:gap-12 lg:gap-[63px] w-full px-container">
          <div className="flex flex-col gap-4 sm:gap-6">
            <h3 className="font-[800] text-[20px] sm:text-[24px] lg:text-[28px] leading-[32px] sm:leading-[40px] lg:leading-[52px]">
              プロダクト改善や共同開発のご相談、
              <br />
              講習の依頼など、お気軽にご連絡ください。
            </h3>
            <p className="font-light text-sm sm:text-base leading-6 sm:leading-8">
              デジタルプロダクトの戦略やソフトウェア開発に関するご相談や、
              <br />
              『unprinted』を活用した広報、マーケティング施策のご相談などメールにて承っております。
            </p>
          </div>

          {/* Contact Button */}
          <a
            href="/contact"
            className="inline-flex items-center gap-2.5 px-4 sm:px-5 py-2.5 border border-black rounded-[30px] bg-white w-fit hover:opacity-70 transition-opacity"
          >
            <span className="font-medium text-base sm:text-lg">
              お問い合わせ
            </span>
            <img
              src="/images/contact-arrow.svg"
              alt=""
              className="w-5 h-5 sm:w-6 sm:h-6"
            />
          </a>
        </div>
      </section>
    </Frame>
  );
};

export default HomePage;

export const Head = ({ data }: Props) => {
  const page = new Page(data.page as any);
  const sd = structuredData(page);

  return <SEO page={page} sd={sd} />;
};

export const query = graphql`
  query HomePage {
    page: pageMdx(filter: { frontmatter: { url: { pathname: { eq: "/" } } } }) {
      id
      frontmatter {
        id
        title
        description
        breadcrumb {
          ...UrlFields
        }
        url {
          ...UrlFields
        }
        type
        releasedAt
        updatedAt
      }
    }
    news: allPageMdx(
      filter: { frontmatter: { type: { eq: "News" } } }
      sort: { frontmatter: { sys: { createdAt: DESC } } }
      limit: 4
    ) {
      nodes {
        id
        frontmatter {
          title
          description
          sys {
            createdAt
          }
          url {
            pathname
          }
        }
      }
    }
    projects: allProjectYaml(
      sort: { order: ASC }
      filter: { sys: { status: { eq: "published" } } }
    ) {
      nodes {
        ...ProjectFields
      }
    }
  }
`;
